import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AlertCircle, CheckCircle2, GripHorizontal, MapPin, Plus } from "lucide-react"
import { AppHeader } from "@/components/dashboard/AppHeader"
import { STAGES } from "@/components/dashboard/data/deals"
import { AddDealDialog } from "@/components/deals/AddDealDialog"
import { Button } from "@/components/ui/button"
import { useDashboardGuard } from "@/lib/activation"
import { useDealPipeline } from "@/lib/deal-pipeline-context"
import { cn } from "@/lib/utils"

const DRAG_MIME = "application/x-sentinel-deal"

const TONE_CLASSES = {
  primary: { color: "text-primary", bg: "bg-primary/10" },
  warning: { color: "text-chart-4", bg: "bg-chart-4/10" },
  destructive: { color: "text-destructive", bg: "bg-destructive/10" },
}

function tier(days, ladder) {
  return ladder.find(([threshold]) => days >= threshold)
}

const SIGNAL_RULES = {
  Identified: () => ({ text: "New deal — enrichment required", tone: "primary" }),
  Appraising: (d) =>
    (d.inactivityDays || 0) > 0
      ? { text: `${d.inactivityDays} days inactive — complete appraisal`, tone: "warning" }
      : { text: "Complete appraisal", tone: "primary" },
  "Offer Ready": (d) => {
    const days = d.inactivityDays || 0
    const match = tier(days, [
      [7, { text: "7+ days — opportunity decaying", tone: "destructive" }],
      [3, { text: "3+ days — start negotiation or reassess", tone: "warning" }],
    ])
    return match?.[1] ?? { text: "Offer ready — start discovery", tone: "primary" }
  },
  "In Negotiation": (d) => {
    const days = d.inactivityDays || 0
    const match = tier(days, [
      [6, { text: "6+ days — deal at risk", tone: "destructive" }],
      [3, { text: "3+ days — follow up required", tone: "warning" }],
    ])
    return match?.[1] ?? { text: "Negotiation active — monitor", tone: "primary" }
  },
  "Follow Up": (d) => {
    const days = d.inactivityDays || 0
    const match = tier(days, [
      [6, { text: `${days} days inactive — deal at risk`, tone: "destructive" }],
      [2, { text: `${days} days inactive — follow up required`, tone: "warning" }],
    ])
    return match?.[1] ?? { text: `Active — ${d.action.toLowerCase()}`, tone: "primary" }
  },
  "Legal / Due Diligence": () => ({ text: "Legal checks — keep moving", tone: "warning" }),
  Completed: () => ({ text: "Deal completed — won", tone: "primary" }),
}

const ACTION_TAB_FOR_STAGE = {
  Appraising: "appraisal",
  "Offer Ready": "negotiation",
  "In Negotiation": "negotiation",
  "Follow Up": "negotiation",
  "Legal / Due Diligence": "negotiation",
  Completed: "negotiation",
}

function scoreClass(score) {
  if (!Number.isFinite(score)) return "text-muted-foreground/80"
  if (score >= 90) return "text-primary"
  if (score >= 80) return "text-foreground"
  return "text-muted-foreground/80"
}

function scoreLabel(score) {
  return Number.isFinite(score) ? score : "Building"
}

function behaviouralSignal(deal) {
  const rule = SIGNAL_RULES[deal.stage] ?? SIGNAL_RULES["Follow Up"]
  const { text, tone } = rule(deal)
  return { text, ...TONE_CLASSES[tone] }
}

function DealCard({ deal, isDragging, onDragStart, onDragEnd, onOpenDeal }) {
  const signal = behaviouralSignal(deal)

  const handleOpen = () => onOpenDeal(deal.id)
  const handleActionOpen = () => onOpenDeal(deal.id, ACTION_TAB_FOR_STAGE[deal.stage])

  return (
    <article
      aria-label={`${deal.address} deal card`}
      draggable
      onDragStart={(e) => onDragStart(e, deal)}
      onDragEnd={onDragEnd}
      className={cn(
        "relative flex cursor-grab flex-col gap-3 rounded-xl border bg-background p-3 shadow-sm transition-all active:cursor-grabbing",
        isDragging ? "scale-95 border-primary/50 opacity-50" : "border-border hover:border-primary/40",
      )}
    >
      <button
        type="button"
        aria-label={`Open ${deal.address}`}
        onClick={handleOpen}
        className="absolute inset-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="relative z-10 flex pointer-events-none items-center gap-3">
        <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-muted">
          <img
            src={deal.image}
            alt={deal.address}
            loading="lazy"
            draggable={false}
            className="h-full w-full object-cover"
          />
        </div>
        <h4 className="flex-1 truncate text-[14px] font-bold leading-tight text-foreground">{deal.address}</h4>
        <GripHorizontal className="hidden h-4 w-4 shrink-0 text-muted-foreground/30 sm:block" />
      </div>

      <div className={cn("relative z-10 flex pointer-events-none w-full items-center justify-center rounded-md border border-current/10 px-2.5 py-2", signal.bg)}>
        <span className={cn("truncate text-[9px] font-bold uppercase tracking-widest", signal.color)}>
          {signal.text}
        </span>
      </div>

      <div className="pointer-events-none relative z-10 flex items-end justify-between pt-1">
        <div className="pointer-events-none flex flex-col gap-1.5">
          <span className="truncate text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
            {deal.stage}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-semibold uppercase leading-none tracking-widest text-muted-foreground/60">
              Score
            </span>
            <span className={cn("font-mono text-sm font-bold leading-none", scoreClass(deal.score))}>
              {scoreLabel(deal.score)}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleActionOpen()
          }}
          className="pointer-events-auto max-w-[130px] truncate rounded border border-primary/20 bg-primary/10 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-widest text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
        >
          {deal.action}
        </button>
      </div>
    </article>
  )
}

function Column({ stage, deals, draggingId, onDragStart, onDragEnd, onDrop, onOpenDeal }) {
  const [isOver, setIsOver] = useState(false)

  const sorted = useMemo(() => {
    return [...deals].sort((a, b) => {
      const ai = a.inactivityDays || 0
      const bi = b.inactivityDays || 0
      if (ai !== bi) return bi - ai
      return b.score - a.score
    })
  }, [deals])

  const allowDrop = (e) => {
    if (Array.from(e.dataTransfer.types).includes(DRAG_MIME)) {
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      if (!isOver) setIsOver(true)
    }
  }

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return
    setIsOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsOver(false)
    onDrop(e, stage)
  }

  return (
    <div
      role="region"
      aria-label={`${stage} deals`}
      onDragOver={allowDrop}
      onDragEnter={allowDrop}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "flex h-full w-[320px] shrink-0 flex-col rounded-xl transition-colors",
        isOver ? "bg-primary/5 ring-2 ring-inset ring-primary/20" : "bg-muted/30",
      )}
    >
      <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between rounded-t-xl border-b border-border/50 bg-background/50 p-3 backdrop-blur-sm">
        <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-foreground">
          {stage}
          {stage === "Completed" ? <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> : null}
        </h3>
        <span className="rounded border border-border/50 bg-background px-1.5 py-0.5 font-mono text-[10px] font-bold text-muted-foreground">
          {deals.length}
        </span>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {sorted.length === 0 ? (
          <div className="flex h-24 items-center justify-center rounded-xl border border-dashed border-border/50 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">
            Empty
          </div>
        ) : (
          sorted.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              isDragging={draggingId === deal.id}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onOpenDeal={onOpenDeal}
            />
          ))
        )}
      </div>
    </div>
  )
}

export function DealPipelineScreen() {
  const guard = useDashboardGuard()
  const navigate = useNavigate()
  const { deals, summary, isLoading, error, addDeal, moveDeal } = useDealPipeline()
  const [isAddDealOpen, setIsAddDealOpen] = useState(false)
  const [draggingId, setDraggingId] = useState(null)

  const grouped = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s, []]))
    for (const d of deals) map[d.stage]?.push(d)
    return map
  }, [deals])

  const counts = useMemo(() => ({
    active: summary.activeDealsCount,
    appraising: summary.appraisingCount,
    offers: summary.offersSubmittedCount,
  }), [summary])

  if (guard) return guard

  const handleDragStart = (e, deal) => {
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ id: deal.id, currentStage: deal.stage }))
    setDraggingId(deal.id)
  }

  const handleDragEnd = () => setDraggingId(null)

  const openDeal = (dealId, tab) => {
    const suffix = tab ? `?tab=${encodeURIComponent(tab)}` : ""
    navigate(`/deal-pipeline/${dealId}${suffix}`)
  }

  const handleDrop = (e, targetStage) => {
    const raw = e.dataTransfer.getData(DRAG_MIME)
    if (!raw) return
    let payload
    try {
      payload = JSON.parse(raw)
    } catch {
      return
    }
    if (!payload?.id || payload.currentStage === targetStage) return
    moveDeal(payload.id, targetStage)
    setDraggingId(null)
  }

  return (
    <main className="flex h-screen flex-col bg-background text-foreground">
      <AppHeader active="deal-pipeline" status="online" planName="Pro" showNav />
      <section className="mx-auto flex w-full min-h-0 max-w-[1600px] flex-1 flex-col p-4 md:p-6 lg:p-8">
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <div className="relative z-10 flex shrink-0 flex-col justify-between gap-4 border-b border-border/50 bg-background p-6 sm:flex-row sm:items-end">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">Deal Pipeline</h1>
              <p className="mb-2 mt-1 text-sm font-medium text-muted-foreground">
                Track and progress deals through the acquisition loop
              </p>
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">
                <AlertCircle className="h-3 w-3" />
                {error ?? "Prioritised by opportunity score and inactivity signals"}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:items-end">
              <Button
                type="button"
                onClick={() => setIsAddDealOpen(true)}
                className="w-fit rounded-lg px-4 py-2 text-xs font-bold uppercase tracking-widest shadow-[0_0_15px_var(--color-primary)] hover:shadow-[0_0_20px_var(--color-primary)]"
              >
                <Plus className="h-4 w-4" /> Add Deal
              </Button>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Maintain minimum pipeline depth to ensure consistent deal flow
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-6 overflow-x-auto border-b border-border/50 bg-muted/20 px-6 py-3">
            <HealthStat dotClass="bg-primary" label="Total Active Deals" value={`${counts.active} / 20`} />
            <span className="h-4 w-px shrink-0 bg-border" />
            <HealthStat dotClass="bg-chart-4" label="Deals in Appraisal" value={counts.appraising} />
            <span className="h-4 w-px shrink-0 bg-border" />
            <HealthStat dotClass="bg-primary/60" label="Offers Submitted (7d)" value={counts.offers} />
          </div>

          <div className="flex-1 overflow-x-auto overflow-y-hidden bg-background/50">
            <div className="flex h-full w-max min-w-full items-start gap-4 p-6">
              {isLoading ? (
                <PipelineState
                  title="Loading pipeline"
                  description="Fetching current deals from Sentinel"
                  onAddDeal={() => setIsAddDealOpen(true)}
                  showAction={false}
                />
              ) : deals.length === 0 ? (
                <div className="flex h-full min-h-[400px] w-full flex-1 flex-col items-center justify-center text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted/50">
                    <MapPin className="h-6 w-6 text-muted-foreground/50" />
                  </div>
                  <h3 className="text-base font-bold text-foreground">Your pipeline is empty</h3>
                  <p className="mb-6 mt-1 text-sm font-medium text-muted-foreground">
                    Add your first deal to begin executing
                  </p>
                  <Button
                    type="button"
                    onClick={() => setIsAddDealOpen(true)}
                    className="rounded-lg px-4 py-2 text-xs font-bold uppercase tracking-widest"
                  >
                    <Plus className="h-4 w-4" /> Add Deal
                  </Button>
                </div>
              ) : (
                STAGES.map((stage) => (
                  <Column
                    key={stage}
                    stage={stage}
                    deals={grouped[stage]}
                    draggingId={draggingId}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                    onOpenDeal={openDeal}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </section>
      <AddDealDialog isOpen={isAddDealOpen} onOpenChange={setIsAddDealOpen} onAddDeal={addDeal} />
    </main>
  )
}

function PipelineState({ title, description, onAddDeal, showAction = true }) {
  return (
    <div className="flex h-full min-h-[400px] w-full flex-1 flex-col items-center justify-center text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted/50">
        <MapPin className="h-6 w-6 text-muted-foreground/50" />
      </div>
      <h3 className="text-base font-bold text-foreground">{title}</h3>
      <p className="mb-6 mt-1 text-sm font-medium text-muted-foreground">
        {description}
      </p>
      {showAction ? (
        <Button
          type="button"
          onClick={onAddDeal}
          className="rounded-lg px-4 py-2 text-xs font-bold uppercase tracking-widest"
        >
          <Plus className="h-4 w-4" /> Add Deal
        </Button>
      ) : null}
    </div>
  )
}

function HealthStat({ dotClass, label, value }) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <span className={cn("h-2 w-2 rounded-full", dotClass)} />
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}:</span>
      <span className="font-mono text-sm font-bold text-foreground">{value}</span>
    </div>
  )
}
